import { useTranslation } from "react-i18next";
import { useEditQuestionForm, useQuestion } from "../hooks";
import { Active, Inactive } from "@pulse/features/Sections";
import { ActiveQuestionLayout, InactiveQuestionLayout } from "../QuestionLayouts";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { faCircleDot, faSquareCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PULSE_FIELD_LIMITS } from "@pulse/features/pulseFieldLimits";
import { Button } from "frontend-components";
import { useState } from "react";
import { QuestionType } from "@pulse/api/surveyApi";

function MultipleChoiceQuestionEdit() {
    const form = useEditQuestionForm();
    const { question } = useQuestion();
    const { t } = useTranslation();
    const isMultiSelect = question.questionType === QuestionType.MULTI_SELECT;
    const [showMinimumOptionsMessage, setShowMinimumOptionsMessage] = useState(false);
    return (
        <>
            <form.AppField name="questionText">
                {(field) => <field.TextArea maxCharacters={PULSE_FIELD_LIMITS.question.text} />}
            </form.AppField>
            <div className="w-full min-w-0">
                <form.AppField name="questionOptions" mode="array">
                    {(field) => {
                        const canRemove = (field.state.value?.length ?? 0) > 2;

                        return (
                            <>
                                <div className="gap-4 flex w-full min-w-0 flex-col mt-2">
                                    {field.state.value?.map((_, index) => (
                                        <form.AppField
                                            key={index}
                                            name={`questionOptions[${index}]`}
                                        >
                                            {(subfield) => (
                                                <subfield.MultipleChoiceInput
                                                    removeFn={() => {
                                                        if (!canRemove) {
                                                            setShowMinimumOptionsMessage(true);
                                                            return;
                                                        }

                                                        field.removeValue(index);
                                                        setShowMinimumOptionsMessage(false);
                                                    }}
                                                    canRemove={canRemove}
                                                    icon={
                                                        isMultiSelect ? faSquareCheck : faCircleDot
                                                    }
                                                />
                                            )}
                                        </form.AppField>
                                    ))}
                                </div>

                                {showMinimumOptionsMessage && (
                                    <div role="status" className="text-compendia-muted text-sm">
                                        {t("question.multipleChoice.validation.atLeastTwoRequired")}
                                    </div>
                                )}

                                {field.state.meta.errors.length > 0 && (
                                    <div className="text-chameleon-pink text-sm">
                                        {t(String(field.state.meta.errors[0]))}
                                    </div>
                                )}

                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => {
                                        field.pushValue(t("question.multipleChoice.option"));
                                        setShowMinimumOptionsMessage(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span>{t("question.multipleChoice.addOption")}</span>
                                </Button>
                            </>
                        );
                    }}
                </form.AppField>
            </div>
        </>
    );
}

function MultipleChoiceQuestionDisplay() {
    const { question } = useQuestion();
    const { t } = useTranslation();
    const isMultiSelect = question.questionType === QuestionType.MULTI_SELECT;

    return (
        <>
            <ul className="flex w-full min-w-0 flex-col gap-2">
                {question.questionOptions && question.questionOptions.length > 0 ? (
                    question.questionOptions.map((option, index) => (
                        <li key={index} className="flex min-w-0 items-center gap-2">
                            <div
                                className={`border-compendia-greige-dark h-4 w-4 border shrink-0 ${
                                    isMultiSelect ? "rounded-xs" : "rounded-full"
                                }`}
                            ></div>
                            <span className="text-compendia-muted min-w-0 flex-1 wrap-anywhere">
                                {option}
                            </span>
                        </li>
                    ))
                ) : (
                    <li className="text-compendia-muted text-sm italic">{t("common.noOptions")}</li>
                )}
            </ul>
        </>
    );
}

export default function MultipleChoiceQuestion() {
    return (
        <>
            <Active>
                <ActiveQuestionLayout>
                    <MultipleChoiceQuestionEdit />
                </ActiveQuestionLayout>
            </Active>
            <Inactive>
                <InactiveQuestionLayout>
                    <MultipleChoiceQuestionDisplay />
                </InactiveQuestionLayout>
            </Inactive>
        </>
    );
}
